import { useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/local.js';
import {
  applyNotificationAction,
  clearNotificationParams,
  readNotificationAction,
  readNotificationView,
} from './handleNotificationAction.js';
import type {
  NotificationAction,
  NotificationTarget,
  NotificationView,
} from './handleNotificationAction.js';
import { NOTIFICATION_CLICK_MESSAGE, takeStoredClick } from './notificationClick.js';

/**
 * Wie lange auf den Abgleich gewartet wird. Danach öffnet die App einfach
 * normal — die offene Zeile steht dann in der Liste. Länger als eine
 * abgebrochene Anfrage (`SYNC_REQUEST_TIMEOUT_MS`) plus eine neue Runde:
 * Hing beim Aufwachen noch eine Anfrage, kommt die Erinnerung erst danach.
 */
export const WAIT_FOR_SYNC_MS = 45_000;

/**
 * Wann nach dem Start noch einmal in die Ablage gesehen wird. Startet iOS die
 * App, bevor der (ebenfalls kalt startende) Service Worker das Antippen
 * abgelegt hat, ist sie beim ersten Blick leer — ein einziger zweiter Blick
 * nach 1,5 s war zu knapp.
 */
export const STORED_CLICK_RETRIES_MS = [1500, 4000, 10_000];

/**
 * Wohin das Antippen einer Benachrichtigung führt.
 *
 * **Die Erinnerung ist beim Antippen meist noch nicht auf dem Gerät.** Der
 * Scheduler legt sie an, während die App geschlossen ist; sie kommt erst mit
 * dem ersten Abgleich nach dem Öffnen. Wer sofort nachschlägt, findet nichts und
 * öffnet stillschweigend nur die App (Geräteabnahme 09.10.2026). Deshalb wird
 * gewartet, bis die Zeile lokal liegt — beobachtet, nicht abgefragt.
 *
 * Drei Eingänge (siehe `notificationClick.ts`): die Adresse beim Kaltstart
 * (`?quittieren=…`), eine Nachricht des Service Workers an die laufende App
 * und die Ablage im Cache, gelesen beim Start und bei jeder Rückkehr in den
 * Vordergrund. Ein Neuladen per `client.navigate()` ist auf iOS kein
 * verlässlicher Weg.
 */
export function useNotificationTarget(
  onTarget: (target: NotificationTarget, action: NotificationAction) => void,
  syncNow: () => void,
  onView: (view: NotificationView) => void = () => undefined,
): void {
  const [pending, setPending] = useState<NotificationAction | null>(() =>
    readNotificationAction(window.location.search),
  );
  const handled = useRef<string | null>(null);
  const lastFollowed = useRef<{ url: string; at: number } | null>(null);

  useEffect(() => {
    /**
     * Ein neues Antippen — auch derselben Erinnerung nach dem Nachfassen.
     *
     * Läuft die App schon, kommt dasselbe Antippen zweimal an: über die Ablage
     * (Rückkehr in den Vordergrund) und als Nachricht. Innerhalb weniger
     * Sekunden zählt dieselbe Adresse deshalb nur einmal — sonst würde eine
     * Aktion wie „Genommen" zweimal ausgeführt.
     */
    const follow = (url: string) => {
      const now = Date.now();
      if (lastFollowed.current?.url === url && now - lastFollowed.current.at < 5000) return;
      lastFollowed.current = { url, at: now };
      const search = new URL(url, window.location.origin).search;
      // Nur eine Ansicht (Wochenrückblick): sofort hin, nichts abzuwarten.
      const view = readNotificationView(search);
      if (view) {
        onView(view);
        return;
      }
      const action = readNotificationAction(search);
      if (!action) return;
      handled.current = null;
      setPending(action);
      syncNow();
    };

    const readStored = () =>
      void takeStoredClick().then((click) => {
        if (click) follow(click.url);
      });

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; url?: unknown } | null;
      if (data?.type !== NOTIFICATION_CLICK_MESSAGE || typeof data.url !== 'string') return;
      // Dasselbe Antippen liegt auch in der Ablage — dort verbrauchen, sonst
      // führte die Rückkehr in den Vordergrund ein zweites Mal hin.
      void takeStoredClick();
      follow(data.url);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') readStored();
    };

    // Beim Start: Kam die App aus einer Mitteilung, ohne dass die Adresse
    // ankam, liegt das Ziel hier.
    // Danach noch einige Male (`STORED_CLICK_RETRIES_MS`).
    const retries: ReturnType<typeof setTimeout>[] = [];
    const startView = readNotificationView(window.location.search);
    if (startView) {
      onView(startView);
      clearNotificationParams();
      void takeStoredClick();
    } else if (!readNotificationAction(window.location.search)) {
      readStored();
      for (const delay of STORED_CLICK_RETRIES_MS) retries.push(setTimeout(readStored, delay));
    } else {
      void takeStoredClick();
    }

    const container = navigator.serviceWorker as ServiceWorkerContainer | undefined;
    container?.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      for (const retry of retries) clearTimeout(retry);
      container?.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [syncNow, onView]);

  // Flach abgefragt (siehe CLAUDE.md): `null` heißt „noch nicht da".
  const arrived = useLiveQuery(
    async () => (pending ? ((await db.reminders.get(pending.reminderId)) ?? null) : null),
    [pending?.reminderId],
    null,
  );

  useEffect(() => {
    if (!pending || !arrived || handled.current === pending.reminderId) return;
    handled.current = pending.reminderId;

    void applyNotificationAction(pending)
      .then((target) => {
        if (target) onTarget(target, pending);
      })
      .finally(() => {
        clearNotificationParams();
        setPending(null);
      });
  }, [pending, arrived, onTarget]);

  // Kommt die Erinnerung nicht (offline, gelöscht), bleibt es beim normalen Start.
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(() => {
      clearNotificationParams();
      setPending(null);
    }, WAIT_FOR_SYNC_MS);
    return () => clearTimeout(timer);
  }, [pending]);
}
